import type { ApolloCache } from '@apollo/client/core';

/**
 * Root query fields whose numbers move when money moves: bills, balances,
 * payments, KPIs, debtors, wallets, discounts and exemptions.
 *
 * The client defaults to `cache-first`, so a billing screen only shows a new
 * payment or bill if the mutation that caused it refreshes that screen's
 * query. Hand-picked `refetchQueries` lists kept missing screens (or used the
 * wrong variables), which is why users had to reload the page to see changes.
 */
export const BILLING_QUERY_FIELDS = [
  'bills',
  'bill',
  'billStudents',
  'billStudentsWithStatus',
  'studentBills',
  'payments',
  'payment',
  'billingKpis',
  'revenueByMonth',
  'paymentMethodBreakdown',
  'debtors',
  'studentFinancials',
  'studentFinancialSummary',
  'studentWallet',
  'studentLedger',
  'refunds',
  'exemptions',
  'entityDiscounts',
  'discountCategories',
  'discountCategory',
  'discountRuleImpact',
  'discountAssignmentPreview',
  'generateBillingReport',
  'formFeeBills',
  'formFeePayers',
] as const;

/** Fee catalogue fields: templates and categories offered when building a bill. */
export const BILL_CATALOGUE_FIELDS = ['billItemTemplates', 'billItemCategories'] as const;

function evictRootFields(cache: ApolloCache<unknown>, fields: readonly string[]): void {
  for (const fieldName of fields) {
    cache.evict({ id: 'ROOT_QUERY', fieldName });
  }
  cache.gc();
}

/*
 * Each helper takes ONLY the cache so it can be passed straight to a mutation
 * as `update` — Apollo calls update(cache, result, options), and any extra
 * parameter here would receive the mutation result.
 */

/**
 * Drops cached billing results after a billing mutation. Queries on screen
 * refetch straight away; screens visited later fetch fresh data instead of
 * serving the old cached numbers.
 */
export function evictBillingQueries(cache: ApolloCache<unknown>): void {
  evictRootFields(cache, BILLING_QUERY_FIELDS);
}

/** After a fee item or category changes: the catalogue lists and their counts. */
export function evictFeeCatalogueQueries(cache: ApolloCache<unknown>): void {
  evictRootFields(cache, BILL_CATALOGUE_FIELDS);
}

/** After bills are created: bills, balances and any fee item saved for reuse on the way. */
export function evictBillingAndCatalogueQueries(cache: ApolloCache<unknown>): void {
  evictRootFields(cache, [...BILL_CATALOGUE_FIELDS, ...BILLING_QUERY_FIELDS]);
}
